/**
 * @file WalletPayButton.tsx
 * @brief Apple Pay / Google Pay for a plan, on the confirmation step.
 *
 * @details
 * Stripe's Express Checkout Element in deferred mode: the wallet sheet shows
 * the plan's charge and that it repeats monthly or yearly; on approval we turn
 * it into a card PaymentMethod (`paymentMethodCreation: "manual"`) and the
 * page subscribes with it exactly like a saved card (POST /billing/subscriptions
 * attaches it and makes it the default), so the server stays the one that
 * charges and confirms. 3D Secure, if the bank asks, follows the usual path.
 *
 * Shows only where a wallet is available: Apple Pay on Safari / iPhone (and
 * only once admin.verblizr.com is registered as a payment method domain in
 * Stripe), Google Pay on Chrome with a saved card. Never inside the Mac app's
 * Electron window, which has neither; the card stays available everywhere.
 *
 * @author DES
 * @date 18-10-2026
 */

import { Elements, ExpressCheckoutElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripeExpressCheckoutElementConfirmEvent } from "@stripe/stripe-js";
import { useState } from "react";
import { getStripe } from "@/shared/stripe/stripeClient";
import type { BillingInterval, CatalogPlan } from "../planConfirm";

type Props = {
  plan: CatalogPlan;
  interval: BillingInterval;
  chargeMinor: number;
  disabled?: boolean;
  /** Subscribe with the wallet's PaymentMethod; resolves false on failure. */
  onPaymentMethod: (paymentMethodId: string) => Promise<boolean>;
};

function WalletButton({ plan, interval, chargeMinor, disabled, onPaymentMethod, onAvailable }: Props & {
  onAvailable: (v: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements || disabled) {
      event.paymentFailed({ reason: "fail" });
      return;
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      event.paymentFailed({ reason: "fail", message: submitError.message });
      return;
    }
    const { error, paymentMethod } = await stripe.createPaymentMethod({ elements });
    if (error || !paymentMethod) {
      event.paymentFailed({ reason: "fail", message: error?.message });
      return;
    }
    // The wallet sheet closes once the PaymentMethod exists; the page then
    // shows its own progress and result.
    void onPaymentMethod(paymentMethod.id);
  };

  return (
    <ExpressCheckoutElement
      options={{
        paymentMethods: { applePay: "auto", googlePay: "auto", link: "never", amazonPay: "never", paypal: "never", klarna: "never" },
        buttonType: { applePay: "subscribe", googlePay: "subscribe" },
        buttonHeight: 44,
        applePay: {
          recurringPaymentRequest: {
            paymentDescription: `VERBLIZR ${plan.name}`,
            managementURL: `${window.location.origin}/app/billing/plan`,
            regularBilling: {
              amount: chargeMinor,
              label: `VERBLIZR ${plan.name}, ${interval === "year" ? "yearly" : "monthly"}`,
              recurringPaymentIntervalUnit: interval === "year" ? "year" : "month",
              recurringPaymentIntervalCount: 1,
            },
          },
        },
      }}
      onReady={(e) => onAvailable(!!e.availablePaymentMethods && Object.values(e.availablePaymentMethods).some(Boolean))}
      onConfirm={(e) => void onConfirm(e)}
    />
  );
}

export function WalletPayButton(props: Props) {
  const [available, setAvailable] = useState(false);
  return (
    <div className={available ? "space-y-2" : "h-0 overflow-hidden"} data-testid="wallet-pay">
      <Elements
        stripe={getStripe()}
        options={{
          mode: "subscription",
          amount: props.chargeMinor,
          currency: props.plan.currency.toLowerCase(),
          setupFutureUsage: "off_session",
          paymentMethodCreation: "manual",
          paymentMethodTypes: ["card"],
        }}
      >
        <WalletButton {...props} onAvailable={setAvailable} />
      </Elements>
      {available ? <p className="text-center text-xs text-muted-foreground">or pay with your saved card</p> : null}
    </div>
  );
}
